import React, { useState } from 'react';
import { formatCurrency } from '../lib/formatters';

export const ReserveSharesPanel = ({ asset, onReserve, loading }) => {
  const [shares, setShares] = useState(1);
  const availableShares = asset.total_shares - asset.sold_shares;

  const quickButtons = [
    { label: '1/4', value: Math.floor(asset.total_shares / 4) },
    { label: '1/2', value: Math.floor(asset.total_shares / 2) },
    { label: 'Full', value: asset.total_shares },
  ];

  const totalAmount = shares * asset.current_price;

  const handleReserve = () => {
    if (shares > availableShares) {
      alert(`Only ${availableShares} shares available`);
      return;
    }
    onReserve(shares);
  };

  return (
    <div
      data-testid="reserve-shares-panel"
      className="bg-white border border-[hsl(var(--border))] rounded-xl p-6 sticky top-24"
    >
      <h3 className="text-lg font-semibold text-[hsl(var(--text-primary))] mb-4">
        Reserve Shares
      </h3>

      {/* Current Price */}
      <div className="mb-6 p-4 bg-[hsl(var(--background-secondary))] rounded-lg">
        <div className="text-sm text-[hsl(var(--text-secondary))] mb-1">Current Price</div>
        <div className="text-2xl font-bold font-mono text-[hsl(var(--text-primary))]">
          {formatCurrency(asset.current_price)}
        </div>
        <div className="text-xs text-[hsl(var(--text-secondary))]">per share</div>
      </div>

      {/* Share Input */}
      <div className="mb-4">
        <label className="text-sm font-medium text-[hsl(var(--text-primary))] mb-2 block">
          Number of Shares
        </label>
        <input
          data-testid="shares-input"
          type="number"
          min="1"
          max={availableShares}
          value={shares}
          onChange={(e) => setShares(Math.max(1, parseInt(e.target.value) || 1))}
          className="w-full px-4 py-3 border border-[hsl(var(--border))] rounded-lg focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-primary))] font-mono text-lg"
        />
        <div className="text-xs text-[hsl(var(--text-secondary))] mt-1">
          Max: {availableShares} shares available
        </div>
      </div>

      {/* Quick Buttons */}
      <div className="mb-6">
        <div className="text-sm font-medium text-[hsl(var(--text-primary))] mb-2">Quick Select</div>
        <div className="grid grid-cols-3 gap-2">
          {quickButtons.map((btn) => (
            <button
              key={btn.label}
              data-testid={`quick-select-${btn.label.toLowerCase().replace('/', '-')}`}
              onClick={() => setShares(Math.min(btn.value, availableShares))}
              disabled={btn.value > availableShares}
              className="px-3 py-2 border border-[hsl(var(--border))] rounded-lg text-sm font-medium text-[hsl(var(--text-primary))] hover:bg-[hsl(var(--background-secondary))] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {btn.label}
            </button>
          ))}
        </div>
      </div>

      {/* Total Amount */}
      <div className="mb-6 p-4 bg-[hsl(var(--brand-primary))] bg-opacity-10 rounded-lg border border-[hsl(var(--brand-primary))]">
        <div className="text-sm text-[hsl(var(--text-secondary))] mb-1">Estimated Total</div>
        <div className="text-3xl font-bold font-mono text-[hsl(var(--brand-primary))]">
          {formatCurrency(totalAmount)}
        </div>
        <div className="text-xs text-[hsl(var(--text-secondary))] mt-1">
          {shares} shares × {formatCurrency(asset.current_price)}
        </div>
      </div>

      {/* Reserve Button */}
      <button
        data-testid="submit-reserve-button"
        onClick={handleReserve}
        disabled={loading || !asset.allow_buy_requests || shares > availableShares}
        className="w-full bg-[hsl(var(--brand-primary))] hover:bg-[hsl(var(--brand-primary-dark))] text-white font-semibold px-6 py-4 rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Processing...' : 'Submit Buy Request'}
      </button>

      <p className="text-xs text-[hsl(var(--text-tertiary))] mt-4 text-center">
        Your request will be reviewed by accounting. Payment will be processed offline.
      </p>
    </div>
  );
};